import React, { useRef, useState } from "react";
import { Divider } from "@nextui-org/react";
import AnimatedHeartIcon from './AnimatedHeartIcon';

interface Position {
    x: number;
    y: number;
}

interface SpotlightCardProps extends React.PropsWithChildren {
    className?: string;
    spotlightColor?: string;
    description?: string;
    docId?: string;
    collectionPath?: string;
    isPlaylist?: boolean;
    isCompleted?: boolean;
    isFavorited?: boolean;
}

const SpotlightCard: React.FC<SpotlightCardProps> = ({
                                                         children,
                                                         className = "",
                                                         spotlightColor,
                                                         description,
                                                         isPlaylist = false,
                                                         isCompleted = false,
                                                         isFavorited = false
                                                     }) => {
    const divRef = useRef<HTMLDivElement>(null);
    const [isFocused, setIsFocused] = useState<boolean>(false);
    const [position, setPosition] = useState<Position>({ x: 0, y: 0 });
    const [opacity, setOpacity] = useState<number>(0);

    // Determine spotlight color based on completion status
    const getSpotlightColor = () => {
        if (spotlightColor) {
            return spotlightColor;
        }

        if (isCompleted) {
            // Using NextUI success color (approximate hex value)
            return "rgba(34, 197, 94, 0.15)"; // success color with opacity
        }

        return "rgba(4, 112, 240, 0.1)"; // Default darkBlue with opacity
    };

    const handleMouseMove: React.MouseEventHandler<HTMLDivElement> = (e) => {
        if (!divRef.current || isFocused) return;

        const rect = divRef.current.getBoundingClientRect();
        setPosition({ x: e.clientX - rect.left, y: e.clientY - rect.top });
    };

    const handleFocus = () => {
        setIsFocused(true);
        setOpacity(0.6);
    };

    const handleBlur = () => {
        setIsFocused(false);
        setOpacity(0);
    };

    const handleMouseEnter = () => {
        setOpacity(0.6);
    };

    const handleMouseLeave = () => {
        setOpacity(0);
    };

    // Determine border color based on completion status
    const borderColor = isCompleted ? 'border-green-500' : 'border-default-200';

    return (
        <div
            ref={divRef}
            onMouseMove={handleMouseMove}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            className={`relative rounded-large border-2 ${borderColor} bg-content1 dark:bg-content1 overflow-hidden shadow-medium transition-all duration-300 ${className}`}
        >
            {/* Spotlight glow effect */}
            <div
                className='pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-500 ease-in-out'
                style={{
                    opacity,
                    background: `radial-gradient(circle at ${position.x}px ${position.y}px, ${getSpotlightColor()}, transparent 80%)`,
                }}
            />
            {/* Favorite Heart Icon */}
            {isFavorited && (
                <div
                    className='absolute z-10 pointer-events-none'
                    style={{ top: '-2px', right: '-1px' }}
                >
                    <AnimatedHeartIcon
                        isFavorited={isFavorited}
                        size={35}
                        speed={0.4}
                        loop={true}
                        animationType='badge'
                    />
                </div>
            )}

            {description && (
                <>
                    <div className='flex gap-3 p-3'>
                        <div className='flex flex-col'>
                            <div className='text-sm text-turquiose dark:text-lightGrey'>
                                <span className='text-black dark:text-lightGrey'>Description:</span> {description}
                            </div>
                        </div>
                    </div>
                    <Divider/>
                </>
            )}

            <div className='p-3'>
                {children}
            </div>
        </div>
    );
};

export default SpotlightCard;